import { useCallback } from 'react';
import * as intrfce from '../Interfaces';
import { createAddressValidationObject } from '../utils/addressUtils';

interface UseAddressModalProps {
  isShipping: boolean;
  currentStep: number;
  formBillingData: intrfce.iBillingFormData;
  formBillingAddress: intrfce.iAddressFormData;
  formData: intrfce.iFormContainer;
  formShippingAddress: intrfce.iAddressFormData;
  recommendedBillingAddress: intrfce.iAddressFormData | null;
  recommendedShippingAddress: intrfce.iAddressFormData | null;
  selectedBillingAddressOption: "yourAddress" | "recommendedAddress";
  selectedAddressOption: "yourAddress" | "recommendedAddress";
  locale: string;
  isBillingFormValid: () => boolean;
  isBillingAddressValid: () => boolean;
  isTaxDataValid: () => boolean;
  isShippingFormValid: () => boolean;
  isShippingAddressValid: () => boolean;
  submitAddress: (addressObj: any, onSuccess: (result: any) => void, onError: (error: any) => void) => void;
  updateSuggestedBillingAddress: (address: intrfce.iSavedAddress) => void;
  updateSuggestedShippingAddress: (address: intrfce.iSavedAddress) => void;
  setShowAddressModal: (show: boolean) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<1 | 2>>;
  setSelectedAddressOption: (option: "yourAddress" | "recommendedAddress") => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<any>>;
  showBodyLoader: () => void;
  hideBodyLoader: () => void;
  handleDualAddressSuccess: (billingResult: any, shippingResult: any) => void;
  fetchCart: () => Promise<void>;
  t: (key: string) => string;
}

export const useAddressModal = (props: UseAddressModalProps) => {
  const {
    isShipping,
    currentStep,
    formBillingData,
    formBillingAddress,
    formData,
    formShippingAddress,
    recommendedBillingAddress,
    recommendedShippingAddress,
    selectedBillingAddressOption,
    selectedAddressOption,
    locale,
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isShippingFormValid,
    isShippingAddressValid,
    submitAddress,
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    setShowAddressModal,
    setCurrentStep,
    setSelectedAddressOption,
    setValidationErrors,
    showBodyLoader,
    hideBodyLoader,
    handleDualAddressSuccess,
    fetchCart,
    t
  } = props;

  /**
   * Creates address entry from form data
   */
  const createAddressEntry = useCallback((
    addressData: intrfce.iAddressFormData,
    contactData: intrfce.iBillingFormData | intrfce.iFormContainer,
    nickName: string,
    isShippingData: boolean = false
  ): intrfce.iSavedAddress => {
    if (isShippingData) {
      const shippingContact = contactData as intrfce.iFormContainer;
      return {
        id: 0,
        city: addressData.cityValue || "",
        country: addressData.countryValue || "US",
        countryName: addressData.countryName || "",
        countrySubdivision: addressData.stateValue || "",
        firstName: shippingContact.firstNameShiping || "",
        lastName: shippingContact.lastNameShiping || "",
        line1: addressData.address || "",
        line2: addressData.line2 || "",
        nickName,
        phoneNumber: shippingContact.contactShiping || "",
        postalCode: addressData.pincode || "",
        isDefault: false,
      };
    } else {
      const billingContact = contactData as intrfce.iBillingFormData;
      return {
        id: 0,
        city: addressData.cityValue || "",
        country: addressData.countryValue || "US",
        countryName: addressData.countryName || "United States",
        countrySubdivision: addressData.stateValue || "",
        firstName: billingContact.firstNameBilling || "",
        lastName: billingContact.lastNameBilling || "",
        line1: addressData.address || "",
        line2: addressData.line2 || "",
        nickName,
        phoneNumber: billingContact.contactBilling || "",
        postalCode: addressData.pincode || "",
        isDefault: false,
      };
    }
  }, []);

  /**
   * Handles address confirmation from modal
   */
  const handleConfirmAddress = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const proceedToStep2 = sessionStorage.getItem('proceedToStep2');
    
    if (proceedToStep2 === 'true') {
      // Apply selected addresses before proceeding to Step 2
      if (isShipping) {
        // Dual address mode
        if (selectedBillingAddressOption === "recommendedAddress" && recommendedBillingAddress) {
          const billingAddressEntry = createAddressEntry(
            recommendedBillingAddress,
            formBillingData,
            "Recommended Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        } else if (selectedBillingAddressOption === "yourAddress") {
          const billingAddressEntry = createAddressEntry(
            formBillingAddress,
            formBillingData,
            "Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        }
        
        if (selectedAddressOption === "recommendedAddress" && recommendedShippingAddress) {
          const shippingAddressEntry = createAddressEntry(
            recommendedShippingAddress,
            formData,
            "Recommended Shipping Address",
            true
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        } else if (selectedAddressOption === "yourAddress") {
          const shippingAddressEntry = createAddressEntry(
            formShippingAddress,
            formData,
            "Shipping Address",
            true
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        }
      } else {
        // Single address mode
        const address = selectedAddressOption === "recommendedAddress" && recommendedShippingAddress
          ? recommendedShippingAddress
          : formBillingAddress;
        const nickName = selectedAddressOption === "recommendedAddress"
          ? "Recommended Address"
          : "Billing Address";
        
        const addressEntry = createAddressEntry(address, formBillingData, nickName);
        updateSuggestedBillingAddress(addressEntry);
        updateSuggestedShippingAddress(addressEntry);
      }
      
      sessionStorage.removeItem('proceedToStep2');
      setShowAddressModal(false);
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // On Step 1, apply addresses and close modal
    if (currentStep === 1) {
      if (isShipping) {
        if (selectedBillingAddressOption === "recommendedAddress" && recommendedBillingAddress) {
          const billingAddressEntry = createAddressEntry(
            recommendedBillingAddress,
            formBillingData,
            "Recommended Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        } else if (selectedBillingAddressOption === "yourAddress") {
          const billingAddressEntry = createAddressEntry(
            formBillingAddress,
            formBillingData,
            "Billing Address"
          );
          updateSuggestedBillingAddress(billingAddressEntry);
        }
        
        if (selectedAddressOption === "recommendedAddress" && recommendedShippingAddress) {
          const shippingAddressEntry = createAddressEntry(
            recommendedShippingAddress,
            formData,
            "Recommended Shipping Address",
            true
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        } else if (selectedAddressOption === "yourAddress") {
          const shippingAddressEntry = createAddressEntry(
            formShippingAddress,
            formData,
            "Shipping Address",
            true
          );
          updateSuggestedShippingAddress(shippingAddressEntry);
        }
      } else {
        const address = selectedAddressOption === "recommendedAddress" && recommendedShippingAddress
          ? recommendedShippingAddress
          : formBillingAddress;
        const nickName = selectedAddressOption === "recommendedAddress"
          ? "Recommended Address"
          : "Billing Address";
        
        const addressEntry = createAddressEntry(address, formBillingData, nickName);
        updateSuggestedBillingAddress(addressEntry);
        updateSuggestedShippingAddress(addressEntry);
      }
      
      setShowAddressModal(false);
      return;
    }
    
    // Default: close modal
    setShowAddressModal(false);
  }, [
    isShipping,
    currentStep,
    formBillingData,
    formBillingAddress,
    formData,
    formShippingAddress,
    recommendedBillingAddress,
    recommendedShippingAddress,
    selectedBillingAddressOption,
    selectedAddressOption,
    createAddressEntry,
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    setShowAddressModal,
    setCurrentStep
  ]);

  /**
   * Handles shipping address update (Edit Address button)
   */
  const handleShippingAddressUpdate = useCallback((
    ev: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (isShipping) {
      // Dual mode: update based on selection
      if (selectedBillingAddressOption === "yourAddress") {
        const updatedBillingAddress = createAddressEntry(
          formBillingAddress,
          formBillingData,
          "Billing Address"
        );
        updateSuggestedBillingAddress(updatedBillingAddress);
      }
      
      if (selectedAddressOption === "yourAddress") {
        const updatedShippingAddress = createAddressEntry(
          formShippingAddress,
          formData,
          "Order Address",
          true
        );
        updateSuggestedShippingAddress(updatedShippingAddress);
      }
    } else {
      // Single mode: update billing
      const updatedBillingAddress = createAddressEntry(
        formBillingAddress,
        formBillingData,
        "Billing Address"
      );
      updateSuggestedBillingAddress(updatedBillingAddress);
    }
    
    setShowAddressModal(false);
  }, [
    isShipping,
    selectedBillingAddressOption,
    selectedAddressOption,
    formBillingAddress,
    formBillingData,
    formShippingAddress,
    formData,
    createAddressEntry,
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    setShowAddressModal
  ]);

  /**
   * Handles address option change in modal
   */
  const handleAddressOptionChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement>,
    option: string
  ) => {
    setSelectedAddressOption(option as "yourAddress" | "recommendedAddress");
  }, [setSelectedAddressOption]);

  /**
   * Handles continue to payment (Step 1 -> Step 2)
   */
  const handleContinueToPayment = useCallback(async () => {
    const step1Valid = isBillingFormValid() && isBillingAddressValid() && isTaxDataValid();
    const shippingValid = !isShipping || (isShippingFormValid() && isShippingAddressValid());
    
    if (step1Valid && shippingValid) {
      showBodyLoader();
      sessionStorage.setItem('proceedToStep2', 'true');
      
      const invalidateRegions = ['BG', 'HR', 'CY', 'EE', 'GR', 'HU', 'LT', 'MT', 'RO', 'SI'];
      
      const billingAddressObj = createAddressValidationObject(
        false,
        formShippingAddress,
        formBillingAddress,
        locale
      );
      
      try {
        let billingResult: any = null;
        if (billingAddressObj?.regionCode && !invalidateRegions.includes(billingAddressObj?.regionCode)) {
          billingResult = await new Promise((resolve) => {
            submitAddress(
              billingAddressObj,
              (result) => resolve(result),
              (err) => { 
                console.log("Billing validation error:", err); 
                resolve(null); 
              }
            );
          });
        }
        
        let shippingResult: any = null;
        if (isShipping) {
          const shippingAddressObj = createAddressValidationObject(
            true,
            formShippingAddress,
            formBillingAddress,
            locale
          );
          
          if (shippingAddressObj?.regionCode && !invalidateRegions.includes(shippingAddressObj?.regionCode)) {
            shippingResult = await new Promise((resolve) => {
              submitAddress(
                shippingAddressObj,
                (result) => resolve(result),
                (err) => { 
                  console.log("Shipping validation error:", err); 
                  resolve(null); 
                }
              );
            });
          }
        }
        
        handleDualAddressSuccess(billingResult, shippingResult);
      } catch (error) {
        console.log("Address validation error:", error);
        hideBodyLoader();
        setShowAddressModal(true);
      }
    } else {
      setValidationErrors((prev: any) => ({
        ...prev,
        ...(!step1Valid && { billingAddress: t('PLEASE_COMPLETE_BILLING_ADDRESS') }),
        ...(!shippingValid && { shippingAddress: t('PLEASE_COMPLETE_SHIPPING_ADDRESS') })
      }));
    }
  }, [
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isShipping,
    isShippingFormValid,
    isShippingAddressValid,
    formShippingAddress,
    formBillingAddress,
    locale,
    submitAddress,
    handleDualAddressSuccess,
    showBodyLoader,
    hideBodyLoader,
    setShowAddressModal,
    setValidationErrors,
    t
  ]);

  /**
   * Handles back to addresses (Step 2 -> Step 1)
   * Reloads cart data to ensure addresses are fresh
   */
  const handleBackToAddresses = useCallback(async () => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Reload cart data to refresh addresses
    await fetchCart();
  }, [setCurrentStep, fetchCart]);

  return {
    handleConfirmAddress,
    handleShippingAddressUpdate,
    handleAddressOptionChange,
    handleContinueToPayment,
    handleBackToAddresses
  };
};

